import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'redux-bundler-react';

import { isValidText, sanitizeText } from '../../../utils/helpers';
import { ErrorMessages, ImpactsActivity } from '@src/utils/enums';
import genericSecondaryModal from '../../../app-pages/Forms/components/modals/genericSecondaryModal';
import { useModal } from '@stores';

const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const requiredActivity = [
  ImpactsActivity.ConversionWatersType,
  ImpactsActivity.DischargeDredgedMaterial,
  ImpactsActivity.DischargeFillMaterial,
  ImpactsActivity.EcologicalRestoration,
];
const disabledActivity = [
  ImpactsActivity.DredgingSection,
  ImpactsActivity.Structure,
  ImpactsActivity.TransportDredgedMaterial,
  ImpactsActivity.Removal,
];

const TypeOfMaterialsTableCell = connect(({ getValue, row, column, table, cell }) => {
  const { openSecondaryModal } = useModal();
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const initialValue = getValue();
  const [value, setValue] = useState(initialValue);
  const [activity, setActivity] = useState();
  const rowActivity = useMemo(() => row.getValue('activity'), [row]);
  const debouncedUpdateRef = useRef();

  useEffect(() => {
    debouncedUpdateRef.current = debounce((newValue) => {
      if (tableMeta?.updateData) {
        tableMeta?.updateData(row.index, column.id, newValue);
      }
    }, 500);
  }, [row.index, column.id, tableMeta?.updateData, columnMeta?.type, tableMeta]);

  const updateValue = useCallback((newValue) => {
    debouncedUpdateRef.current(newValue);
  }, []);

  useEffect(() => {
    // Reset value when activity value makes this cell disabled/unavailable
    if (disabledActivity.includes(activity)) {
      setValue(null);
      updateValue(null);
    }
  }, [activity, updateValue]);

  useEffect(() => {
    rowActivity && setActivity(rowActivity);
  }, [rowActivity]);

  const handleChange = (e) => {
    setValue(e?.target?.value);
    updateValue(e?.target?.value);
  };

  // Handling special characters
  useEffect(() => {
    if (value && typeof value === 'string') {
      if (isValidText(value)) {
        setValue(value);
        updateValue(value);
      } else {
        // Optionally handle invalid input here, such as warning the user
        const msg = ErrorMessages.InvalidCharacters;
        openSecondaryModal(genericSecondaryModal, { title: 'Invalid Characters', msg: msg });
        setValue(sanitizeText(value));
        updateValue(sanitizeText(value));
      }
    }
  }, [value, setValue, updateValue, openSecondaryModal]);

  return (
    <input
      aria-label='Material type'
      disabled={disabledActivity.includes(activity)}
      id={cell.id}
      maxLength={4000}
      onChange={handleChange}
      placeholder={disabledActivity.includes(activity) ? 'N/A' : ''}
      required={requiredActivity.includes(activity)}
      style={{
        width: '100%',
        borderColor: 'hsl(0, 0%, 80%)',
        cursor: columnMeta?.readOnly || disabledActivity.includes(activity) ? 'not-allowed' : 'auto',
      }}
      type='text'
      value={value ?? ''}
    />
  );
});

export default TypeOfMaterialsTableCell;
